import { useEffect, useState } from 'react';
import { Shield, Star, Heart, Crown, TrendingUp, RefreshCw } from 'lucide-react';
import api from '@/lib/api';

/**
 * Phase 2D — Reputation Card.
 * Subtle, social-trust framing. NEVER Uber-style ratings.
 * Renders an empty/build-state for new players, full stat strip + badges for veterans.
 */
const BADGE_VISUALS = {
  reliable_player:           { icon: Shield, color: '#10b981', accent: 'rgba(16,185,129,0.16)' },
  frequently_invited_back:   { icon: Heart,  color: '#ec4899', accent: 'rgba(236,72,153,0.16)' },
  mvp_tier:                  { icon: Star,   color: '#f59e0b', accent: 'rgba(245,158,11,0.16)' },
  squad_loyal:               { icon: Crown,  color: '#a855f7', accent: 'rgba(168,85,247,0.16)' },
  trusted:                   { icon: TrendingUp, color: '#06b6d4', accent: 'rgba(6,182,212,0.16)' },
};

export default function ArenaxReputationCard({ playerId = null }) {
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchRep = (fresh = false) => {
    const url = playerId
      ? `/arenax/reputation/${playerId}`
      : `/arenax/reputation/me${fresh ? '?fresh=true' : ''}`;
    setLoading(true);
    api.get(url)
      .then(r => setRep(r.data))
      .catch(() => setRep(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchRep(); /* eslint-disable-next-line */ }, [playerId]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-800 p-5 animate-pulse" data-testid="arenax-reputation-card-loading"
        style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.10), var(--ax-card-bg-soft))' }}>
        <div className="h-3 w-32 bg-slate-800 rounded mb-3" />
        <div className="h-7 w-48 bg-slate-800 rounded" />
      </div>
    );
  }
  if (!rep) return null;

  const isOwn = !playerId;
  const games = rep.games_played || 0;
  const att = rep.attendance_rate;
  const wpa = rep.wpa_received_pct;
  const mvp = rep.mvp_votes_received || 0;
  const badges = rep.badges || [];
  const hasNarrative = badges.length > 0;

  // New-player state — encouraging, social, never punitive
  if (games === 0) {
    return (
      <div className="rounded-2xl border border-cyan-500/20 p-6"
        data-testid="arenax-reputation-card-empty"
        style={{ background: 'linear-gradient(160deg, rgba(6,182,212,0.07), var(--ax-card-bg-soft))' }}>
        <div className="flex items-center gap-2 mb-3">
          <Shield className="h-4 w-4 text-cyan-300" />
          <span className="text-[11px] tracking-[0.3em] text-cyan-300 font-bold">YOUR TRUST GRAPH</span>
        </div>
        <h3 className="text-xl font-bold text-slate-100 mb-2">Your reputation builds with every game.</h3>
        <p className="text-sm text-slate-400 leading-relaxed">
          Show up, play fair, vote MVPs you respect. Your trust signals start the day you finish your first game.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-emerald-500/15 p-6"
      data-testid="arenax-reputation-card"
      style={{ background: 'linear-gradient(160deg, rgba(16,185,129,0.06), var(--ax-card-bg-soft))' }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-emerald-300" />
          <span className="text-[11px] tracking-[0.3em] text-emerald-300 font-bold">
            {isOwn ? 'YOUR TRUST GRAPH' : 'TRUST GRAPH'}
          </span>
        </div>
        {isOwn && (
          <button onClick={() => fetchRep(true)} data-testid="arenax-reputation-refresh"
            className="text-[10px] text-slate-500 hover:text-cyan-300 flex items-center gap-1">
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        )}
      </div>

      {/* Narrative headline — if we have a badge, weave it into the title for that human feel */}
      {hasNarrative ? (
        <h3 className="text-2xl font-bold text-slate-100 mb-1" data-testid="arenax-reputation-narrative">
          {badges[0].label}
          <span className="text-slate-400 font-normal text-base ml-2">· {badges[0].hint}</span>
        </h3>
      ) : (
        <h3 className="text-2xl font-bold text-slate-100 mb-1">Player in good standing</h3>
      )}
      <p className="text-xs text-slate-500 mb-5">{games} game{games !== 1 ? 's' : ''} played · trust grows with every match.</p>

      {/* Stat strip — quiet but informative */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <Stat label="Attendance" value={att !== null && att !== undefined ? `${Math.round(att)}%` : '—'} color="#10b981" testid="arenax-rep-attendance" />
        <Stat label="Would Play Again" value={wpa !== null && wpa !== undefined ? `${Math.round(wpa)}%` : '—'} color="#ec4899" testid="arenax-rep-wpa" />
        <Stat label="MVP Nods" value={mvp || '—'} color="#f59e0b" testid="arenax-rep-mvp" />
      </div>

      {/* Badge constellation — only secondary badges (primary went into the headline) */}
      {badges.length > 1 && (
        <div className="flex flex-wrap gap-2" data-testid="arenax-reputation-badges">
          {badges.slice(1).map(b => {
            const v = BADGE_VISUALS[b.key] || BADGE_VISUALS.trusted;
            const Icon = v.icon;
            return (
              <div key={b.key}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs"
                style={{ borderColor: v.color + '40', background: v.accent }}
                data-testid={`arenax-reputation-badge-${b.key}`}>
                <Icon className="h-3 w-3" style={{ color: v.color }} />
                <span className="text-slate-200 font-bold">{b.label}</span>
                <span className="text-slate-400">· {b.hint}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color, testid }) {
  return (
    <div className="text-center" data-testid={testid}>
      <div className="text-2xl font-black tabular-nums" style={{ color }}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mt-0.5">{label}</div>
    </div>
  );
}
